/**
 * JudgmentAdvisoryPanel — translates Judgment Index profiles into prioritization
 * guidance a facilitator can act on.
 *
 * Advisory only: never weights, gates, or alters anyone's input. It surfaces
 * where a person's (or a group's) judgment is a dependable strength and where
 * it is likely a blind spot when comparing and ranking items.
 */

import { useMemo } from 'react';
import { Lightbulb, AlertTriangle, ShieldCheck, Info, Scale } from 'lucide-react';
import type { JudgmentIndexProfile } from '../../../types/prioritization/judgmentIndex';
import { aggregateTeam, profileAdvisories } from '../../../lib/judgmentIndexModel';

export function JudgmentAdvisoryPanel({
  profiles,
  title = 'How this judgment mix shapes prioritization',
  compact = false,
}: {
  profiles: JudgmentIndexProfile[];
  title?: string;
  compact?: boolean;
}) {
  const aggregate = useMemo(() => aggregateTeam(profiles), [profiles]);

  if (profiles.length === 0) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <div className="flex items-start gap-3 text-sm text-[var(--text-ter)]">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
          <p>
            No Judgment Index profiles recorded yet. Add profiles to see how the group's judgment
            mix is likely to shape how items get compared and ranked.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="text-base font-semibold text-[var(--text-pri)]">{title}</h3>
      </div>
      <p className="text-xs text-[var(--text-ter)] mb-4">
        Advisory only — based on {profiles.length} recorded {profiles.length === 1 ? 'profile' : 'profiles'}.
        Never changes scores, weights, or rankings.
      </p>

      <p className="text-sm text-[var(--text-sec)] mb-4">{aggregate.balanceSummary}</p>

      {aggregate.collectiveBlindSpots.length > 0 && (
        <div className="mb-4 border border-[var(--coral)] bg-[var(--coral-dim)] p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--coral)] mb-1">
            <AlertTriangle className="h-4 w-4" /> Watch-outs for this group
          </div>
          <ul className="space-y-1 text-sm text-[var(--coral)]/90 list-disc pl-5">
            {aggregate.collectiveBlindSpots.map((s) => (
              <li key={s.key}>
                <span className="font-medium">{s.label}</span> is collectively low
                {s.lowCount > 0 ? ` (${s.lowCount} of ${profiles.length})` : ''} —
                consider assigning an owner or dedicating extra deliberation where that lens matters.
              </li>
            ))}
          </ul>
        </div>
      )}

      {aggregate.balanceWatchOuts.length > 0 && (
        <div className="mb-4 border border-[var(--mustard)] bg-[var(--mustard-dim)] p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--mustard)] mb-1">
            <Scale className="h-4 w-4" /> Balance watch-outs
          </div>
          <ul className="space-y-1 text-sm text-[var(--mustard)]/90 list-disc pl-5">
            {aggregate.balanceWatchOuts.map((b) => (
              <li key={b.key}>
                <span className="font-medium">{b.label}</span>{' '}
                {b.status === 'over' ? 'may be over-valued' : 'may be under-valued'} on average
                ({b.meanValue > 0 ? `+${b.meanValue}` : b.meanValue}).
              </li>
            ))}
          </ul>
        </div>
      )}

      {aggregate.collectiveStrengths.length > 0 && (
        <div className="mb-4 border border-[var(--green)] bg-[var(--green-dim)] p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] mb-1">
            <ShieldCheck className="h-4 w-4" /> Lean on these strengths
          </div>
          <ul className="space-y-1 text-sm text-[var(--green)]/90 dark:text-[var(--text-ter)]/90 list-disc pl-5">
            {aggregate.collectiveStrengths.map((s) => (
              <li key={s.key}>
                <span className="font-medium">{s.label}</span> is a shared strength — a reliable
                lens to anchor comparisons on.
              </li>
            ))}
          </ul>
        </div>
      )}

      {!compact && <PerPersonAdvisories profiles={profiles} />}
    </div>
  );
}

function PerPersonAdvisories({ profiles }: { profiles: JudgmentIndexProfile[] }) {
  const rows = profiles
    .map((p) => ({ profile: p, advisories: profileAdvisories(p) }))
    .filter((r) => r.advisories.length > 0);

  if (rows.length === 0) return null;

  return (
    <div className="border-t border-[var(--rule)] pt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">Per-person notes</h4>
      <div className="space-y-3">
        {rows.map(({ profile, advisories }) => (
          <div key={profile.id}>
            <div className="text-sm font-medium text-[var(--text-pri)]">
              {profile.subjectName}
              {profile.judgmentType && (
                <span className="ml-1.5 text-xs font-normal text-[var(--text-ter)]">
                  Type {profile.judgmentType}{profile.judgmentTypeName ? ` — ${profile.judgmentTypeName}` : ''}
                </span>
              )}
            </div>
            <ul className="mt-1 space-y-1">
              {advisories.map((a, i) => (
                <li key={`${a.key}-${i}`} className="flex items-start gap-2 text-sm">
                  <span className={`mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                    a.kind === 'strength'   ? 'bg-[var(--green)]' :
                    a.kind === 'blind_spot' ? 'bg-[var(--coral)]'    : 'bg-[var(--mustard)]'
                  }`} />
                  <span className="text-[var(--text-sec)]">
                    <span className="text-[var(--text-pri)]">{a.label}:</span> {a.message}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default JudgmentAdvisoryPanel;
